/*
  Messages: the parent's one thread with the ClassTutor team. Layout borrowed from the team inbox
  (day pills, bubbles, meta line) with the sides flipped: the parent on the right in Liberty purple,
  the team on the left in white. "Closed" and "Reopened" are dotted dividers, as asked.
*/
import { SendOutlined } from "@ant-design/icons";
import { TopBar } from "app/components/Shell";
import Spinner from "app/components/Spinner";
import { useGetChatQuery, useMarkReadMutation, useSendMessageMutation } from "app/services/chat";
import { liveState } from "app/services/chatStream";
import { useMarkAnnouncementsReadMutation } from "app/services/notifications";
import type { ChatMessage } from "app/services/types";
import storage, { STORAGE_KEYS } from "app/utils/storage";
import { chatDay } from "app/utils/time";
import moment from "moment";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";

const TEAM_HOURS = "We reply during office hours, usually within a few hours.";

export default function Messages() {
  // The Shell keeps the live stream open; this screen only reads the shared query.
  const { data, isLoading, refetch } = useGetChatQuery(undefined, { pollingInterval: liveState.connected ? 60000 : 5000, refetchOnFocus: true });
  const [markAnnouncements] = useMarkAnnouncementsReadMutation();
  const [send, { isLoading: sending }] = useSendMessageMutation();
  const [markRead] = useMarkReadMutation();
  const [text, setText] = useState("");
  const bottom = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const lastCount = useRef(0);

  const messages = data?.messages ?? [];
  const lastId = messages.length ? Math.max(...messages.filter((m) => m.id > 0).map((m) => m.id)) : 0;

  // Keep the newest message in view: on first load (after layout settles) and whenever one arrives.
  useEffect(() => {
    if (messages.length === lastCount.current) return;
    lastCount.current = messages.length;
    const toBottom = () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "auto" });
    requestAnimationFrame(toBottom);
    const t = setTimeout(toBottom, 250);
    return () => clearTimeout(t);
  }, [messages.length]);

  useEffect(() => {
    if (!lastId) return;
    const read = Number(storage.getItem(STORAGE_KEYS.CHAT_READ) || 0);
    if (lastId > read) { storage.setItem(STORAGE_KEYS.CHAT_READ, String(lastId)); void markRead({ upTo: lastId }); }
    if (messages.some((m) => m.broadcast)) void markAnnouncements({});
  }, [lastId, markRead, markAnnouncements, messages]);

  const grouped = useMemo(() => {
    const out: Array<{ day: string; items: ChatMessage[] }> = [];
    for (const m of messages) {
      const day = chatDay(m.at);
      if (!out.length || out[out.length - 1].day !== day) out.push({ day, items: [] });
      out[out.length - 1].items.push(m);
    }
    return out;
  }, [messages]);

  const submit = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setText("");
    box.current?.focus();
    const clientId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    try { await send({ text: body, clientId }).unwrap(); } catch { /* shown as failed in the thread */ }
    void refetch();
  };

  return (
    <div className="ct-page" style={{ paddingBottom: "calc(var(--ct-nav-height) + 104px + var(--ct-safe-bottom))" }}>
      <TopBar title="Messages" />
      <div className="ct-card p-3 mb-3 d-flex gap-3 align-items-center">
        <span style={{ width: 40, height: 40, borderRadius: "50%", background: "#eceefa", color: "#4c54a0", display: "grid", placeItems: "center", fontWeight: 800 }}>CT</span>
        <div>
          <div style={{ fontWeight: 700, color: "#171725" }}>ClassTutor team</div>
          <div className="ct-muted" style={{ fontSize: 12 }}>{data?.status === "closed" ? "This conversation is closed. Send a message to reopen it." : TEAM_HOURS}</div>
        </div>
        <span className={`ct-live ${liveState.connected ? "on" : ""}`} title={liveState.connected ? "Connected: replies appear instantly" : "Reconnecting"}><i />{liveState.connected ? "Live" : "Connecting"}</span>
      </div>

      {isLoading && !data ? <Spinner /> : !messages.length ? (
        <div className="text-center ct-muted py-5">
          <div style={{ fontWeight: 700, color: "#44444f" }}>Say hello</div>
          <div style={{ fontSize: 13 }}>Ask about a lesson, a payment, or anything else. Everything you send here reaches the whole team.</div>
        </div>
      ) : grouped.map((g) => (
        <Fragment key={g.day}>
          <div className="ct-day-sep"><span>{g.day}</span></div>
          {g.items.map((m) => (
            m.kind === "closed" || m.kind === "reopened" ? (
              <div key={m.id} className="ct-marker" role="separator">{m.kind === "closed" ? "Conversation closed" : "Conversation reopened"}{m.agentName && m.kind === "closed" ? ` by ${m.agentName.split(" ")[0]}` : ""} · {moment(m.at).format("h:mma")}</div>
            ) : (
              <div key={m.clientId ?? m.id} className={`ct-row ${m.from === "me" ? "me" : "team"}`}>
                <div style={{ minWidth: 0 }}>
                  <div className={`ct-bubble ${m.broadcast ? "announce" : ""}`}>
                    {m.broadcast ? <div className="ct-announce-tag">Announcement{m.title ? ` · ${m.title}` : ""}</div> : null}
                    {m.text}
                  </div>
                  <div className="ct-mline">
                    {m.from === "team" && m.agentName && <span style={{ fontWeight: 600 }}>{m.agentName.split(" ")[0]}</span>}
                    <span>{moment(m.at).format("h:mma")}</span>
                    {m.from === "me" && (m.failed ? <span style={{ color: "#fc5a5a", fontWeight: 600 }}>Not sent</span> : m.pending ? <span>Sending</span> : m.seen ? <span style={{ color: "#45b1b2", fontWeight: 600 }}>Seen</span> : <span>Sent</span>)}
                  </div>
                </div>
              </div>
            )
          ))}
        </Fragment>
      ))}
      <div ref={bottom} />

      <form className="ct-composer" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <textarea ref={box} rows={1} value={text} placeholder="Write a message" aria-label="Write a message" maxLength={4000}
          onChange={(e) => { setText(e.target.value); e.target.style.height = "auto"; e.target.style.height = `${Math.min(120, e.target.scrollHeight)}px`; }}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) void submit(); }} />
        <button type="submit" className="btn btn-primary rounded-circle d-grid" style={{ width: 44, height: 44, placeItems: "center", flexShrink: 0 }} disabled={!text.trim() || sending} aria-label="Send"><SendOutlined /></button>
      </form>
    </div>
  );
}
